/**
 * Shared helpers for the Catalogue and Swatch request forms.
 *
 * Loaded by both sections/catalogue-request.liquid and sections/swatch-request.liquid, hence the idempotence guard.
 */
(() => {
  if (window.aureloRequestForms) return;

  const onReady = (fn) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn, { once: true });
    } else {
      fn();
    }
  };

  /**
   * Reveals the free-text field paired with a select when "Other" is chosen.
   * The pairing is declared in the markup with data-toggle-hidden on the select
   * and data-hidden-field on the wrapper, and is scoped to the closest form so
   * two forms on one page don't fight over the same value.
   */
  const initConditionalFields = () => {
    document.querySelectorAll("[data-toggle-hidden]").forEach((select) => {
      const scope = select.closest("form") || document;
      const target = scope.querySelector(`[data-hidden-field="${select.dataset.toggleHidden}"]`);
      if (!target) return;

      select.addEventListener("change", () => {
        target.classList.toggle("hidden", select.value !== "Other");
      });
    });
  };

  /**
   * Restricts a set of inputs to digits and a single decimal point, restoring
   * the previous value on an invalid keystroke.
   */
  const initNumberInputs = (selector = ".number-input") => {
    const events = ["input", "keydown", "keyup", "mousedown", "mouseup", "select", "contextmenu", "drop"];
    const isNumeric = (value) => /^\d*\.?\d*$/.test(value);

    document.querySelectorAll(selector).forEach((input) => {
      events.forEach((event) => {
        input.addEventListener(event, () => {
          if (isNumeric(input.value)) {
            input.dataset.oldValue = input.value;
            input.dataset.oldSelectionStart = input.selectionStart;
            input.dataset.oldSelectionEnd = input.selectionEnd;
          } else if ("oldValue" in input.dataset) {
            input.value = input.dataset.oldValue;
            input.setSelectionRange(Number(input.dataset.oldSelectionStart), Number(input.dataset.oldSelectionEnd));
          } else {
            input.value = "";
          }
        });
      });
    });
  };

  const KLAVIYO_TIMEOUT = 2000;

  /**
   * Identify then track in Klaviyo, calling back once tracking has completed.
   * Falls straight through to the callback if Klaviyo isn't on the page, and
   * after KLAVIYO_TIMEOUT if it never calls back, so a blocked or missing
   * script can't swallow the submission.
   */
  const klaviyoTrack = (customerData, data, callback) => {
    if (typeof klaviyo === "undefined") {
      console.warn(`Klaviyo not loaded; "${data.event}" not tracked. Is the Klaviyo app embed enabled?`);
      callback();
      return;
    }

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      callback();
    };

    const timer = setTimeout(() => {
      console.warn(`Klaviyo did not respond within ${KLAVIYO_TIMEOUT}ms; "${data.event}" may not have been tracked.`);
      finish();
    }, KLAVIYO_TIMEOUT);

    // eslint-disable-next-line no-undef
    klaviyo.identify(customerData, () => {
      // eslint-disable-next-line no-undef
      klaviyo.track(data.event, data.trackData, finish);
    });
  };

  /**
   * POSTs form-encoded data, matching what jQuery.post sent in the Aurelo theme:
   * application/x-www-form-urlencoded with bracketed keys for nested objects,
   * which keeps it a CORS-simple request (no preflight).
   */
  const postForm = (url, data) => {
    const body = new URLSearchParams();

    Object.entries(data).forEach(([key, value]) => {
      if (value === null || value === undefined) return;

      if (typeof value === "object") {
        Object.entries(value).forEach(([subKey, subValue]) => {
          body.append(`${key}[${subKey}]`, subValue);
        });
      } else {
        body.append(key, value);
      }
    });

    return fetch(url, { method: "POST", body });
  };

  /** Replaces jQuery's fadeIn/fadeOut on the swatch limit warning. */
  const setVisible = (el, visible) => {
    if (el) el.classList.toggle("hidden", !visible);
  };

  window.aureloRequestForms = {
    onReady,
    initConditionalFields,
    initNumberInputs,
    klaviyoTrack,
    postForm,
    setVisible,
  };

  onReady(() => {
    initConditionalFields();
    initNumberInputs();
  });
})();
